'use client';

import { useEffect, useState, useCallback } from 'react';
import { plannerCounselSlotApi, type PlannerCounselSlotResponse } from './planner-counsel-slot.api';
import { timeslotToDateTime } from '@/shared/lib/timeslot/timeslot.types';
import { usePlanners } from './usePlanners';

/**
 * UI용 슬롯 구조
 */
export interface ScheduleSlot {
  time: string; // HH:mm 형식
  status: 'AVAILABLE' | 'APPOINTED' | 'UNAVAILABLE';
  visitCounselId?: string | null;
}

/**
 * UI용 스케줄 레코드
 */
export interface AggregatedScheduleRecord {
  id: string;
  plannerId: string;
  plannerName: string;
  date: string;
  regionId: string;
  regionName: string;
  slots: ScheduleSlot[];
}

export interface UsePlannerSchedulesOptions {
  date?: string; // YYYY-MM-DD 형식 (단일 날짜)
  dates?: string[]; // 여러 날짜 (배열) - 범위 조회를 위해 첫/마지막 날짜 추출 시 사용
  startDate?: string; // YYYY-MM-DD 형식 (범위 시작)
  endDate?: string; // YYYY-MM-DD 형식 (범위 끝)
  regionId?: string; // 지역 코드
  plannerId?: string; // 플래너 ID
  enabled?: boolean;
  useCheckpoint?: boolean; // checkpoint 기반 증분 조회 사용 여부 (기본값: false)
}

export interface UsePlannerSchedulesResult {
  schedules: AggregatedScheduleRecord[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

function convertTimeslotToTime(timeslot: string): string {
  try {
    const dt = timeslotToDateTime(timeslot as any);
    return dt.format('HH:mm');
  } catch (err) {
    console.error('Failed to convert timeslot:', timeslot, err);
    return '00:00';
  }
}

function convertSlotsToUI(apiSlot: PlannerCounselSlotResponse): ScheduleSlot[] {
  if (!apiSlot.slots || apiSlot.slots.length === 0) return [];

  return apiSlot.slots.map((slot) => ({
    time: convertTimeslotToTime(slot.timeslot),
    status: slot.status,
    visitCounselId: slot.visitCounselId,
  }));
}

/**
 * 플래너 스케줄 데이터를 집계하는 훅
 * API의 PlannerCounselSlotResponse를 UI용 AggregatedScheduleRecord로 변환
 */
export function usePlannerSchedules(
  options: UsePlannerSchedulesOptions = {}
): UsePlannerSchedulesResult {
  const {
    date,
    dates,
    startDate,
    endDate,
    regionId,
    plannerId,
    enabled = true,
    useCheckpoint = false,
  } = options;

  const [schedules, setSchedules] = useState<AggregatedScheduleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [checkpoint, setCheckpoint] = useState<number | null>(null);
  const [plannerIds, setPlannerIds] = useState<string[]>([]);

  const { planners: cachedPlanners } = usePlanners({ ids: plannerIds });

  const fetchAggregatedData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let apiSlots: PlannerCounselSlotResponse[] = [];
      let queryStartDate: string | undefined = startDate;
      let queryEndDate: string | undefined = endDate;

      if (!queryStartDate && !queryEndDate && dates && dates.length > 0) {
        queryStartDate = dates[0];
        queryEndDate = dates[dates.length - 1];
      }

      if (plannerId) {
        if (queryStartDate && queryEndDate) {
          const slots = await plannerCounselSlotApi.getSlotsByPlannerAndDateRange(
            plannerId,
            queryStartDate,
            queryEndDate
          );
          apiSlots = slots;
        } else if (date) {
          const slots = await plannerCounselSlotApi.getSlotsByPlannerAndDate(plannerId, date);
          apiSlots = slots;
        }
      } else {
        let response: any;
        const checkpointParam = useCheckpoint ? checkpoint || undefined : undefined;

        if (queryStartDate && queryEndDate) {
          response = await plannerCounselSlotApi.getAllSlotsByDateRange(
            queryStartDate,
            queryEndDate,
            checkpointParam,
            regionId
          );
        } else if (date) {
          response = await plannerCounselSlotApi.getAllSlots(date, checkpointParam, regionId);
        }

        if (response && response.slots) {
          apiSlots = response.slots;
          if (useCheckpoint && response.checkpoint) {
            setCheckpoint(response.checkpoint.sequence);
          }
        } else {
          apiSlots = response || [];
        }
      }

      let filteredSlots = apiSlots;
      if (regionId) {
        filteredSlots = apiSlots.filter((slot) => slot.regionId === regionId);
      }

      const extractedPlannerIds = Array.from(new Set(filteredSlots.map((slot) => slot.plannerId)));
      setPlannerIds(extractedPlannerIds);

      const plannerMap = new Map(cachedPlanners.map((p) => [p.id, p]));

      const newAggregated: AggregatedScheduleRecord[] = filteredSlots.map((slot, index) => {
        const planner = plannerMap.get(slot.plannerId);
        return {
          id: `schedule-${slot.plannerId}-${slot.date}-${index}`,
          plannerId: slot.plannerId,
          plannerName: planner?.name || '알 수 없음',
          date: slot.date,
          regionId: slot.regionId,
          regionName: slot.regionId,
          slots: convertSlotsToUI(slot),
        };
      });

      if (useCheckpoint && checkpoint !== null && checkpoint > 0) {
        if (newAggregated.length > 0) {
          setSchedules((prev) => {
            const merged = [...prev];
            newAggregated.forEach((newRecord) => {
              const existingIndex = merged.findIndex(
                (m) => m.plannerId === newRecord.plannerId && m.date === newRecord.date
              );
              if (existingIndex >= 0) {
                merged[existingIndex] = newRecord;
              } else {
                merged.push(newRecord);
              }
            });
            return merged;
          });
        }
      } else {
        setSchedules(newAggregated);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch slots');
      setError(error);
      console.error('Aggregate slots error:', error);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [
    date,
    dates,
    startDate,
    endDate,
    plannerId,
    regionId,
    enabled,
    checkpoint,
    useCheckpoint,
    cachedPlanners,
  ]);

  useEffect(() => {
    if (cachedPlanners.length > 0 && schedules.length > 0) {
      const plannerMap = new Map(cachedPlanners.map((p) => [p.id, p]));
      setSchedules((prev) =>
        prev.map((schedule) => {
          const planner = plannerMap.get(schedule.plannerId);
          return {
            ...schedule,
            plannerName: planner?.name || '알 수 없음',
          };
        })
      );
    }
  }, [cachedPlanners]);

  useEffect(() => {
    if (useCheckpoint) {
      setCheckpoint(null);
    }
  }, [startDate, endDate, date, dates, regionId, plannerId, useCheckpoint]);

  useEffect(() => {
    fetchAggregatedData();
  }, [fetchAggregatedData]);

  return {
    schedules,
    loading,
    error,
    refetch: fetchAggregatedData,
  };
}
